import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebarNavigation from '../../components/ui/AdminSidebarNavigation';
import AdminHeaderNavigation from '../../components/ui/AdminHeaderNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import CategorySection from './components/CategorySection';
import ServiceModal from './components/ServiceModal';
import CategoryModal from './components/CategoryModal';
import BulkActionsBar from './components/BulkActionsBar';
import repositoryContainer from '../../services/RepositoryContainer';
import { useAuth } from '../../components/context/AuthContext';
import { useToast } from '../../components/context/ToastContext';

const ServicesManagement = () => {
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [draggingService, setDraggingService] = useState(null);
  const [selectedServices, setSelectedServices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [areHeaderActionsVisible, setAreHeaderActionsVisible] = useState(true);
  const [isQuickAddOpen, setIsQuickAddOpen] = useState(false);
  const headerActionsRef = useRef(null);

  const [categories, setCategories] = useState([]);
  const { user, logout } = useAuth();
  const [services, setServices] = useState([]);
  const { addToast } = useToast();
  
  const handleLogout = async () => {
    await logout();
  };

// Create category (admin only)
const createCategory = async (name, icon) => {
  try {
    const response = await repositoryContainer.category.create({ name, icon });
    return response;
  } catch (error) {
    console.error('Failed to create category:', error);
  }
};

// Update category (admin only)
const updateCategory = async (id, name, icon) => {
  try {
    const response = await repositoryContainer.category.update(id, { name, icon });
    return response;
  } catch (error) {
    console.error('Failed to update category:', error);
  }
};


const loadData = async () => {
    setIsLoading(true);
    try {
      // Load categories and services in parallel
      const [categoriesData, servicesData] = await Promise.all([
        repositoryContainer.category.list(true),
        repositoryContainer.service.list({ activeOnly: true })
      ]);
      
      setCategories(categoriesData);
      // enrich services with category name for display
      const enriched = servicesData.map(s => ({
        ...s,
        category: categoriesData.find(c => c.id === s.categoryId)?.name || ''
      }));
      setServices(enriched);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateService = async (serviceData) => {
    try {
      await repositoryContainer.service.create(serviceData);
      await loadData();
      addToast('Service created successfully.', 'success');
    } catch (error) {
      console.error('Failed to create service:', error);
      addToast(error?.data?.message || 'Failed to create service.', 'error');
      throw error;
    }
  };

  const handleUpdateService = async (id, serviceData) => {
    try {
      await repositoryContainer.service.update(id, serviceData);
      await loadData();
      addToast('Service updated successfully.', 'success');
    } catch (error) {
      console.error('Failed to update service:', error);
      addToast(error?.data?.message || 'Failed to update service.', 'error');
      throw error;
    }
  };

  const handleDeletingService = async (id) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;

    try {
      await repositoryContainer.service.delete(id);
      await loadData();
      addToast('Service deleted successfully.', 'success');
    } catch (error) {
      console.error('Failed to delete service:', error);
      addToast(error?.data?.message || 'Failed to delete service.', 'error');
      throw error;
    }
  };

  const handleAddService = () => {
    setEditingService(null);
    setIsServiceModalOpen(true);
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const actions = headerActionsRef.current;
    if (!actions || typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => setAreHeaderActionsVisible(entry.isIntersecting),
      { threshold: 0.1 }
    );

    observer.observe(actions);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (areHeaderActionsVisible || isServiceModalOpen || isCategoryModalOpen) {
      setIsQuickAddOpen(false);
    }
  }, [areHeaderActionsVisible, isServiceModalOpen, isCategoryModalOpen]);

  const handleEditService = (service) => {
    setEditingService(service);
    setIsServiceModalOpen(true);
  };

  const handleSaveService = async (serviceData) => {
    try {
      const payload = {
        name: serviceData.name,
        description: serviceData.description,
        price: parseFloat(serviceData.price),
        durationInMinutes: serviceData.type === 'service' ? parseInt(serviceData.duration, 10) : undefined,
        categoryId: serviceData.categoryId,
        type: serviceData.type || 'service',
        pricingMode: serviceData.type === 'service' ? serviceData.pricingMode : 'fixed',
        ...(serviceData.type === 'service' && serviceData.pricingMode === 'range' && {
          minimumPrice: parseFloat(serviceData.minimumPrice),
          maximumPrice: parseFloat(serviceData.maximumPrice),
        }),
        imageUrl: serviceData.image,
        isPopular: !!serviceData.isPopular,
        ...(serviceData.type === 'product' && serviceData.initialStock != null && {
          initialStock: serviceData.initialStock,
        }),
        ...(serviceData.lowStockThreshold != null && {
          lowStockThreshold: serviceData.lowStockThreshold,
        }),
      };

      if (editingService) {
        await handleUpdateService(editingService.id, payload);
      } else {
        await handleCreateService(payload);
      }

      // reload fresh data from server
      await loadData();
      setIsServiceModalOpen(false);
    } catch (error) {
      console.error('Failed to save service from modal:', error);
      addToast(error?.data?.message || 'Failed to save service', 'error');
      // fallback to local update (enrich category name)
      const catName = categories?.find(c => c?.id === serviceData?.categoryId)?.name || '';
      const localItem = {
        ...serviceData,
        category: catName,
        durationInMinutes: serviceData?.type === 'service' ? parseInt(serviceData?.duration, 10) : undefined,
        imageUrl: serviceData?.image
      };
      if (editingService) {
        setServices((prev) => prev?.map((s) => s?.id === serviceData?.id ? localItem : s));
      } else {
        setServices((prev) => [...prev, localItem]);
      }
      setIsServiceModalOpen(false);
    }
  };

  const handleAddCategory = () => {
    setEditingCategory(null);
    setIsCategoryModalOpen(true);
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setIsCategoryModalOpen(true);
  };

  const handleSaveCategory = async (categoryData) => {
    try {
      if (editingCategory) {
        await updateCategory(editingCategory.id, categoryData.name, categoryData.icon);
      } else {
        await createCategory(categoryData.name, categoryData.icon);
      }

      await loadData();
      setIsCategoryModalOpen(false);
    } catch (error) {
      console.error('Failed to save category from modal:', error);
        addToast(error?.data?.message || 'Failed to save category', 'error');
      // fallback to local update
      if (editingCategory) {
        setCategories((prev) => prev?.map((c) => c?.id === categoryData?.id ? categoryData : c));
      } else {
        setCategories((prev) => [...prev, categoryData]);
      }
      setIsCategoryModalOpen(false);
    }
  };

  const handleDeleteCategory = async(categoryId) => {
    const category = categories?.find((c) => c?.id === categoryId);
    const hasServices = services?.some((s) => s?.category === category?.name);

    if (hasServices) {
      addToast('Cannot delete category with existing services. Please reassign or delete services first.', 'error');
      return;
    }

     if (!window.confirm('Are you sure you want to delete this category?')) return;

  try {
    await repositoryContainer.category.delete(categoryId);
    setCategories((prev) => prev?.filter((c) => c?.id !== categoryId));
    addToast('Category deleted successfully.', 'success');
  } catch (error) {
    console.error('Failed to delete category:', error);
    addToast('Failed to delete category. Please try again.', 'error');
  }
  };

  const handleDragStart = (e, service) => {
    setDraggingService(service);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDraggingService(null);
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetCategoryId) => {
    e?.preventDefault();
    if (!draggingService) return;

    const targetCategory = categories?.find((c) => c?.id === targetCategoryId);
    if (!targetCategory) return;

    try {
      // update on server first
      await handleUpdateService(draggingService.id, {
        ...draggingService,
        categoryId: targetCategoryId
      });
      // reload to get latest names
      await loadData();
    } catch (err) {
      console.error('Failed to move service to new category:', err);
      // fallback local
      setServices((prev) => prev?.map((s) =>
        s?.id === draggingService?.id ?
        { ...s, category: targetCategory?.name, categoryId: targetCategoryId } :
        s
      ));
    }

    setDraggingService(null);
  };

  const filteredServices = services?.filter((service) => {
    const matchesSearch = service?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    service?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesCategory = filterCategory === 'all' || service?.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const getServicesByCategory = (categoryName) => {
    return filteredServices?.filter((s) => s?.category === categoryName);
  };

  const categoryOptions = [
  { value: 'all', label: 'All Categories' },
  ...categories?.map((cat) => ({ value: cat?.name, label: cat?.name }))];


    const [isRestoring, setIsRestoring] = useState(false);

  const handleRestore = async () => {
    setIsRestoring(true);
    try {
      await repositoryContainer.company.restoreBusiness();
      // Force a reload to refresh the user context and token from the backend
      window.location.reload(); 
    } catch (err) {
      console.error('Failed to restore business:', err);
      alert('Could not restore the business. Please contact support.');
      setIsRestoring(false);
    }
  };

if (user?.company && user.company.isActive === false) {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=DM+Sans:wght@300;400;500;700&display=swap');

        .archive-container {
          font-family: 'DM Sans', sans-serif;
          color: #fff;
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
          position: relative;
          background: #000;
        }

        .bg-overlay {
          position: absolute;
          inset: 0;
          background-image: url('https://weyyfslmfqjlkvtfwunm.supabase.co/storage/v1/object/public/Claw/Herosection/heroImage.png');
          background-size: cover;
          background-position: center;
          filter: blur(15px) brightness(0.4);
          opacity: 0.6;
          z-index: 0;
        }

        .archive-card {
          position: relative;
          z-index: 10;
          width: 100%;
          max-width: 480px;
          background: rgba(255, 255, 255, 0.03);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 24px;
          padding: 48px 32px;
          text-align: center;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
        }

        .status-icon-wrap {
          width: 80px;
          height: 80px;
          background: rgba(255, 255, 255, 0.05);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 24px;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .archive-badge {
          display: inline-block;
          padding: 4px 12px;
          background: rgba(234, 179, 8, 0.1);
          border: 1px solid rgba(234, 179, 8, 0.3);
          color: #eab308;
          border-radius: 99px;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          margin-bottom: 20px;
        }

        .archive-title {
          font-family: 'DM Serif Display', serif;
          font-size: 32px;
          margin-bottom: 12px;
          letter-spacing: -0.02em;
        }

        .archive-desc {
          font-size: 15px;
          color: rgba(255, 255, 255, 0.6);
          line-height: 1.6;
          margin-bottom: 32px;
          font-weight: 300;
        }

        .info-row {
          background: rgba(255, 255, 255, 0.03);
          border-radius: 12px;
          padding: 16px;
          text-align: left;
          margin-bottom: 32px;
          border: 1px solid rgba(255, 255, 255, 0.05);
        }

        .info-item {
          display: flex;
          gap: 12px;
          font-size: 13px;
          color: rgba(255, 255, 255, 0.8);
          margin-bottom: 8px;
        }

        .restore-btn {
          width: 100%;
          background: #fff;
          color: #000;
          border: none;
          padding: 16px;
          border-radius: 12px;
          font-weight: 700;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s;
          margin-bottom: 12px;
        }

        .restore-btn:hover {
          background: rgba(255, 255, 255, 0.9);
          transform: translateY(-2px);
        }

        .signout-btn {
          width: 100%;
          background: transparent;
          color: rgba(255, 255, 255, 0.5);
          border: 1px solid rgba(255, 255, 255, 0.1);
          padding: 16px;
          border-radius: 12px;
          font-weight: 500;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .signout-btn:hover {
          background: rgba(255, 255, 255, 0.05);
          color: #fff;
        }
      `}</style>

      <div className="archive-container">
        <div className="bg-overlay" />
        
        <div className="archive-card">
          <div className="status-icon-wrap">
            <Icon name="Archive" size={32} color="rgba(255,255,255,0.7)" />
          </div>

          <div className="archive-badge">Status: Archived</div>
          
          <h1 className="archive-title">Workspace Offline</h1>
          <p className="archive-desc">
            Your business profile for <strong>{user?.company?.name || 'your store'}</strong> has been deactivated. All public booking services and dashboard tools are currently restricted.
          </p>

          <div className="info-row">
            <div className="info-item">
              <Icon name="EyeOff" size={16} color="#eab308" />
              <span>Public booking page is hidden</span>
            </div>
            <div className="info-item">
              <Icon name="Database" size={16} color="#eab308" />
              <span>Data is preserved for compliance</span>
            </div>
            <div className="info-item">
              <Icon name="HelpCircle" size={16} color="#eab308" />
              <span>Contact support@clawappz.com</span>
            </div>
          </div>

          <button className="restore-btn" onClick={handleRestore} disabled={isRestoring}>
            {isRestoring ? 'Processing Restoration...' : 'Reactivate Business'}
          </button>
          
          <button className="signout-btn" onClick={logout}>
            Log Out
          </button>
        </div>
      </div>
    </>
  );
}


  return (
    <div className="min-h-screen bg-background">
      <AdminSidebarNavigation
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)} />

      <div className={`transition-all duration-250 ease-elegant ${isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        <AdminHeaderNavigation
          userName="Sarah Johnson"
          onLogout={handleLogout}
          isSidebarCollapsed={isSidebarCollapsed}
        />

        
        <main className="pt-24 px-4 md:px-6 lg:px-8 pb-8">
          <div className="max-w-7xl mx-auto">
            <div className="mb-6 md:mb-8">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div>
                  <h1 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-foreground mb-2">
                    Services & Products
                  </h1>
                  <p className="text-sm md:text-base text-muted-foreground font-caption">
                    Manage your services, products, pricing, and categories
                  </p>
                </div>
                <div ref={headerActionsRef} className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    iconName="FolderPlus"
                    iconPosition="left"
                    iconSize={18}
                    onClick={handleAddCategory}>
                    <span className="hidden sm:inline">Add Category</span>
                  </Button>
                  <Button
                    variant="default"
                    iconName="Plus"
                    iconPosition="left"
                    iconSize={18}
                    onClick={handleAddService}>

                    Add Item
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-5 mb-6">
                <div className="bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 rounded-xl p-3 sm:p-4 md:p-5">
                  <div className="flex items-center justify-between mb-1 sm:mb-2">
                    <Icon name="Sparkles" size={24} color="var(--color-primary)" className="h-5 w-5 sm:h-6 sm:w-6" />
                    <span className="text-[10px] sm:text-xs font-caption text-primary bg-primary/10 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full">
                      Services
                    </span>
                  </div>
                  <p className="font-heading font-bold text-xl sm:text-2xl md:text-3xl text-foreground mb-1">
                    {services?.filter(s => s?.type !== 'product')?.length}
                  </p>
                  <p className="text-xs md:text-sm text-muted-foreground font-caption">
                    Active Services
                  </p>
                </div>

                <div className="bg-gradient-to-br from-secondary/10 to-secondary/5 border border-secondary/20 rounded-xl p-3 sm:p-4 md:p-5">
                  <div className="flex items-center justify-between mb-1 sm:mb-2">
                    <Icon name="Package" size={24} color="var(--color-secondary)" className="h-5 w-5 sm:h-6 sm:w-6" />
                    <span className="text-[10px] sm:text-xs font-caption text-secondary bg-secondary/10 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full">
                      Products
                    </span>
                  </div>
                  <p className="font-heading font-bold text-xl sm:text-2xl md:text-3xl text-foreground mb-1">
                    {services?.filter(s => s?.type === 'product')?.length}
                  </p>
                  <p className="text-xs md:text-sm text-muted-foreground font-caption">
                    Active Products
                  </p>
                </div>

                <div className="bg-gradient-to-br from-accent/10 to-accent/5 border border-accent/20 rounded-xl p-3 sm:p-4 md:p-5">
                  <div className="flex items-center justify-between mb-1 sm:mb-2">
                    <Icon name="Star" size={24} color="var(--color-accent)" className="h-5 w-5 sm:h-6 sm:w-6" />
                    <span className="text-[10px] sm:text-xs font-caption text-accent bg-accent/10 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full">
                      Featured
                    </span>
                  </div>
                  <p className="font-heading font-bold text-xl sm:text-2xl md:text-3xl text-foreground mb-1">
                    {services?.filter((s) => s?.isPopular)?.length}
                  </p>
                  <p className="text-xs md:text-sm text-muted-foreground font-caption">
                    Popular Items
                  </p>
                </div>

                <div className="bg-gradient-to-br from-success/10 to-success/5 border border-success/20 rounded-xl p-3 sm:p-4 md:p-5">
                  <div className="flex items-center justify-between mb-1 sm:mb-2">
                    <Icon name="FolderOpen" size={24} color="var(--color-success)" className="h-5 w-5 sm:h-6 sm:w-6" />
                    <span className="text-[10px] sm:text-xs font-caption text-success bg-success/10 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full">
                      Groups
                    </span>
                  </div>
                  <p className="font-heading font-bold text-xl sm:text-2xl md:text-3xl text-foreground mb-1">
                    {categories?.length}
                  </p>
                  <p className="text-xs md:text-sm text-muted-foreground font-caption">
                    Categories
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
                <div className="flex-1">
                  <Input
                    type="search"
                    placeholder="Search services..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e?.target?.value)}
                    className="w-full" />

                </div>
                <div className="w-full sm:w-64">
                  <Select
                    options={categoryOptions}
                    value={filterCategory}
                    onChange={setFilterCategory}
                    placeholder="Filter by category" />

                </div>
              </div>
            </div>

            <div className="space-y-4 md:space-y-5 lg:space-y-6">
              {categories?.map((category) => {
                const categoryServices = getServicesByCategory(category?.name);
                if (categoryServices?.length === 0 && filterCategory !== 'all') return null;

                return (
                  <CategorySection
                    key={category?.id}
                    category={category}
                    services={categoryServices}
                    onEditCategory={handleEditCategory}
                    onDeleteCategory={handleDeleteCategory}
                    onEditService={handleEditService}
                    onDeleteService={handleDeletingService}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    draggingService={draggingService} />);


              })}

              {filteredServices?.length === 0 &&
              <div className="text-center py-12 md:py-16 lg:py-20">
                  <Icon name="Search" size={64} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
                  <h3 className="font-heading font-semibold text-xl md:text-2xl text-foreground mb-2">
                    No items found
                  </h3>
                  <p className="text-sm md:text-base text-muted-foreground font-caption mb-6">
                    Try adjusting your search or filter criteria
                  </p>
                  <Button
                  variant="outline"
                  iconName="RotateCcw"
                  iconPosition="left"
                  iconSize={18}
                  onClick={() => {
                    setSearchQuery('');
                    setFilterCategory('all');
                  }}>

                    Clear Filters
                  </Button>
                </div>
              }
            </div>
          </div>
        </main>
      </div>
      <ServiceModal
        isOpen={isServiceModalOpen}
        onClose={() => setIsServiceModalOpen(false)}
        onSave={handleSaveService}
        service={editingService}
        categories={categories} />

      <CategoryModal
        isOpen={isCategoryModalOpen}
        onClose={() => setIsCategoryModalOpen(false)}
        onSave={handleSaveCategory}
        category={editingCategory} />

      {!areHeaderActionsVisible && !isServiceModalOpen && !isCategoryModalOpen && (
        <div className="fixed bottom-5 right-4 z-40 flex flex-col items-end gap-2 sm:bottom-6 sm:right-6">
          {isQuickAddOpen && (
            <div className="flex flex-col items-end gap-2 animate-scale-in">
              <button
                type="button"
                onClick={() => {
                  setIsQuickAddOpen(false);
                  handleAddCategory();
                }}
                className="flex h-11 items-center gap-2 rounded-full border border-border bg-card px-4 text-sm font-medium text-foreground shadow-lg transition-colors hover:bg-muted font-caption"
              >
                <Icon name="FolderPlus" size={18} />
                Add Category
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsQuickAddOpen(false);
                  handleAddService();
                }}
                className="flex h-11 items-center gap-2 rounded-full bg-primary px-4 text-sm font-medium text-primary-foreground shadow-lg transition-colors hover:bg-primary/90 font-caption"
              >
                <Icon name="Plus" size={18} />
                Add Item
              </button>
            </div>
          )}
          <button
            type="button"
            onClick={() => setIsQuickAddOpen((current) => !current)}
            aria-label={isQuickAddOpen ? 'Close quick add menu' : 'Open quick add menu'}
            aria-expanded={isQuickAddOpen}
            className="flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-xl transition-all hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            <Icon
              name={isQuickAddOpen ? 'X' : 'Plus'}
              size={24}
              className="transition-transform duration-200"
            />
          </button>
        </div>
      )}

      <BulkActionsBar
        selectedCount={selectedServices?.length}
        onClearSelection={() => setSelectedServices([])}
        onBulkPriceUpdate={() => alert('Bulk price update feature coming soon')}
        onBulkCategoryChange={() => alert('Bulk category change feature coming soon')}
        onBulkDelete={() => {
          if (window.confirm(`Delete ${selectedServices?.length} selected services?`)) {
            setServices((prev) => prev?.filter((s) => !selectedServices?.includes(s?.id)));
            setSelectedServices([]);
          }
        }} />

    </div>);

};

export default ServicesManagement;
